// src/components/ThreadDetailView.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Button,
  Chip,
  Divider,
  IconButton,
  Snackbar,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  Person as PersonIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiFetch from '../utils/api';

const MESSAGE_TYPE = {
  SYSTEM: 1,
  CHAT: 2,
  FROM_USER: 3,
  TO_USER: 4,
  COMMAND: 6,
  SYSTEM_TO_USER: 7,
  EDITED: 8,
  DELETED: 9,
};

function renderMarkdown(text) {
  if (!text) return text;
  // Split on **bold** and *italic* patterns, returning React elements
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Match **bold** first
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Match *italic* (but not **)
    const italicMatch = remaining.match(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/);

    let nextMatch = null;
    let type = null;

    if (boldMatch && italicMatch) {
      if (boldMatch.index <= italicMatch.index) {
        nextMatch = boldMatch;
        type = 'bold';
      } else {
        nextMatch = italicMatch;
        type = 'italic';
      }
    } else if (boldMatch) {
      nextMatch = boldMatch;
      type = 'bold';
    } else if (italicMatch) {
      nextMatch = italicMatch;
      type = 'italic';
    }

    if (!nextMatch) {
      parts.push(remaining);
      break;
    }

    // Add text before the match
    if (nextMatch.index > 0) {
      parts.push(remaining.substring(0, nextMatch.index));
    }

    if (type === 'bold') {
      parts.push(<strong key={key++}>{nextMatch[1]}</strong>);
    } else {
      parts.push(<em key={key++}>{nextMatch[1]}</em>);
    }

    remaining = remaining.substring(nextMatch.index + nextMatch[0].length);
  }

  return parts;
}

/**
 * Normalize attachment URLs: if the URL points to an internal/dashboard host,
 * extract just the /attachments/... path so it resolves relative to the current origin.
 */
function normalizeAttachmentUrl(url) {
  try {
    const parsed = new URL(url);
    if (parsed.pathname.startsWith('/attachments/')) {
      return parsed.pathname;
    }
  } catch {
    // not a valid URL, return as-is
  }
  return url;
}

function getMessageStyle(messageType) {
  const base = {
    maxWidth: '50%',
    p: 2,
  };

  switch (messageType) {
    case MESSAGE_TYPE.FROM_USER:
      return {
        ...base,
        alignSelf: 'flex-end',
        bgcolor: 'rgba(25, 118, 210, 0.12)',
        borderRight: '3px solid #42a5f5',
        borderRadius: '16px 4px 16px 16px',
      };
    case MESSAGE_TYPE.TO_USER:
      return {
        ...base,
        alignSelf: 'flex-start',
        bgcolor: 'rgba(67, 160, 71, 0.12)',
        borderLeft: '3px solid #66bb6a',
        borderRadius: '4px 16px 16px 16px',
      };
    case MESSAGE_TYPE.SYSTEM:
    case MESSAGE_TYPE.SYSTEM_TO_USER:
      return {
        ...base,
        alignSelf: 'flex-start',
        bgcolor: 'rgba(255, 152, 0, 0.10)',
        borderLeft: '3px solid #ffa726',
        borderRadius: '4px 16px 16px 16px',
      };
    case MESSAGE_TYPE.COMMAND:
      return {
        ...base,
        alignSelf: 'flex-start',
        bgcolor: 'rgba(123, 31, 162, 0.15)',
        borderLeft: '3px solid #ab47bc',
        borderRadius: '4px 16px 16px 16px',
      };
    case MESSAGE_TYPE.DELETED:
      return {
        ...base,
        alignSelf: 'flex-start',
        bgcolor: 'rgba(211, 47, 47, 0.10)',
        textDecoration: 'line-through',
        borderLeft: '3px solid #ef5350',
        borderRadius: '4px 16px 16px 16px',
      };
    case MESSAGE_TYPE.EDITED:
      return {
        ...base,
        alignSelf: 'flex-start',
        bgcolor: 'rgba(255, 152, 0, 0.12)',
        borderLeft: '3px solid #ffa726',
        borderRadius: '4px 16px 16px 16px',
      };
    default:
      return {
        ...base,
        alignSelf: 'flex-start',
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        borderLeft: '3px solid rgba(255,255,255,0.2)',
        borderRadius: '4px 16px 16px 16px',
      };
  }
}

function getChipProps(messageType) {
  switch (messageType) {
    case MESSAGE_TYPE.FROM_USER:
      return { sx: { ml: 1, height: 20, fontSize: '0.7rem', bgcolor: '#1976d2', color: '#fff' } };
    case MESSAGE_TYPE.TO_USER:
      return { sx: { ml: 1, height: 20, fontSize: '0.7rem', bgcolor: '#43a047', color: '#fff' } };
    case MESSAGE_TYPE.SYSTEM:
    case MESSAGE_TYPE.SYSTEM_TO_USER:
      return { sx: { ml: 1, height: 20, fontSize: '0.7rem', bgcolor: '#ef6c00', color: '#fff' } };
    case MESSAGE_TYPE.COMMAND:
      return { sx: { ml: 1, height: 20, fontSize: '0.7rem', bgcolor: '#7b1fa2', color: '#fff' } };
    case MESSAGE_TYPE.EDITED:
      return { sx: { ml: 1, height: 20, fontSize: '0.7rem', bgcolor: '#ff9800', color: '#fff' } };
    case MESSAGE_TYPE.DELETED:
      return { sx: { ml: 1, height: 20, fontSize: '0.7rem', bgcolor: '#d32f2f', color: '#fff' } };
    default:
      return { sx: { ml: 1, height: 20, fontSize: '0.7rem' } };
  }
}

function getMessageLabel(messageType) {
  switch (messageType) {
    case MESSAGE_TYPE.FROM_USER:
      return 'User';
    case MESSAGE_TYPE.TO_USER:
      return 'Staff';
    case MESSAGE_TYPE.SYSTEM:
      return 'System';
    case MESSAGE_TYPE.SYSTEM_TO_USER:
      return 'System';
    case MESSAGE_TYPE.COMMAND:
      return 'Command';
    case MESSAGE_TYPE.EDITED:
      return 'Edited';
    case MESSAGE_TYPE.DELETED:
      return 'Deleted';
    default:
      return '';
  }
}

function ThreadDetailView() {
  const { threadId } = useParams();
  const navigate = useNavigate();

  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    async function fetchThread() {
      setLoading(true);
      try {
        const res = await apiFetch(`/api/logs/${threadId}`, {
          credentials: 'include',
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to fetch thread');
        }

        setThread(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchThread();
  }, [threadId]);

  const handleVisibilityToggle = async () => {
    if (!thread || updating) return;
    setUpdating(true);

    try {
      const res = await apiFetch(`/api/logs/${threadId}/visibility`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isPublic: !thread.isPublic }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update visibility');
      }

      const data = await res.json();
      setThread((prev) => ({ ...prev, isPublic: data.isPublic }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleCopyLink = () => {
    const publicUrl = `${window.location.origin}/logs/${threadId}/public`;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setSnackbar({ open: true, message: 'Public link copied to clipboard' });
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/logs')} sx={{ mb: 2 }}>
          Back to Logs
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!thread) return null;

  const userInitial = thread.userName ? thread.userName.charAt(0).toUpperCase() : '?';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/logs')} sx={{ mb: 2 }}>
        Back to Logs
      </Button>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }} variant="outlined">
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: '#5c6bc0', fontSize: '1.5rem' }}>
              {userInitial}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Thread #{thread.threadNumber}
              </Typography>
              <Typography variant="subtitle1">
                {thread.userName}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                {thread.userId}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={thread.isPublic || false}
                  onChange={handleVisibilityToggle}
                  disabled={updating}
                />
              }
              label={thread.isPublic ? 'Public' : 'Private'}
            />
            {thread.isPublic && (
              <IconButton onClick={handleCopyLink} title="Copy public link">
                <CopyIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" flexWrap="wrap" gap={4}>
          {thread.createdAt && (
            <Box>
              <Typography variant="caption" color="textSecondary">Opened</Typography>
              <Typography variant="body2">
                {format(new Date(thread.createdAt), 'MMMM d, yyyy \'at\' HH:mm')}
              </Typography>
            </Box>
          )}
          {thread.botInstanceId && typeof thread.botInstanceId === 'object' && (
            <Box>
              <Typography variant="caption" color="textSecondary">Bot</Typography>
              <Typography variant="body2">{thread.botInstanceId.name}</Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="textSecondary">Messages</Typography>
            <Typography variant="body2">{thread.messages ? thread.messages.length : 0}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Messages */}
      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid', borderColor: 'divider' }} variant="outlined">
        <Box display="flex" flexDirection="column" gap={2}>
          {thread.messages && thread.messages.length > 0 ? (
            thread.messages.map((msg, idx) => {
              const style = getMessageStyle(msg.messageType);
              const label = getMessageLabel(msg.messageType);
              const chipProps = getChipProps(msg.messageType);
              const displayName = msg.isAnonymous ? 'Staff' : msg.userName;

              return (
                <Box key={idx} sx={style}>
                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Typography variant="caption" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                      {displayName}
                    </Typography>
                    {label && (
                      <Chip label={label} size="small" {...chipProps} />
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {renderMarkdown(msg.body)}
                  </Typography>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {msg.attachments.map((rawUrl, aIdx) => {
                        const url = normalizeAttachmentUrl(rawUrl);
                        const isImage = /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url);
                        return isImage ? (
                          <Box
                            key={aIdx}
                            component="a"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'block', maxWidth: 300 }}
                          >
                            <Box
                              component="img"
                              src={url}
                              alt="attachment"
                              sx={{ maxWidth: '100%', borderRadius: 1, display: 'block' }}
                            />
                          </Box>
                        ) : (
                          <Box
                            key={aIdx}
                            component="a"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: 'primary.main',
                              fontSize: '0.8rem',
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            <AttachFileIcon sx={{ fontSize: 14 }} />
                            {decodeURIComponent(url.split('/').pop().split('?')[0]) || 'Attachment'}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {msg.createdAt && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      title={format(new Date(msg.createdAt), 'MMMM d, yyyy \'at\' HH:mm:ss')}
                      sx={{ fontSize: '0.65rem', opacity: 0.7, mt: 0.5, display: 'block', textAlign: 'right' }}
                    >
                      {format(new Date(msg.createdAt), 'MMM d, HH:mm')}
                    </Typography>
                  )}
                </Box>
              );
            })
          ) : (
            <Typography color="textSecondary" textAlign="center" py={2}>
              No messages in this thread.
            </Typography>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  );
}

export default ThreadDetailView;
